import React, { useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight } from "lucide-react";
import AuthShell from "../components/AuthShell";
import KodPole, { PoslatZnovu } from "../components/KodPole";
import { Button, Field, Spinner } from "../components/ui/kit";
import { useAuth } from "../context/AuthContext";
import api, { apiError } from "../lib/api";
import { ROLE_LABEL } from "../lib/nav";

const BARVY = ["#2b7a6f", "#c2347a", "#3a6fd8", "#d07a1d", "#7d5bd0", "#3c8c86"];
const barva = (s) => BARVY[[...(s || "")].reduce((a, c) => a + c.charCodeAt(0), 0) % BARVY.length];
const inicialy = (s) => {
  const slova = (s || "?").replace(/,?\s*(s\.\s?r\.\s?o\.|a\.\s?s\.|spol\..*)$/i, "").trim().split(/\s+/);
  return (slova.length > 1 ? slova[0][0] + slova[1][0] : slova[0].slice(0, 2)).toUpperCase();
};

export function ZnakFirmy({ nazev, logo, velikost = 42 }) {
  if (logo) {
    return <img src={logo} alt="" className="rounded-[12px] bg-white object-contain shrink-0 shadow-[inset_0_0_0_1px_#eceae5]"
      style={{ width: velikost, height: velikost }} />;
  }
  return (
    <span className="rounded-[12px] text-white font-800 inline-flex items-center justify-center shrink-0"
      style={{ width: velikost, height: velikost, background: barva(nazev), fontSize: velikost * 0.33 }}>{inicialy(nazev)}</span>
  );
}

/**
 * Přihlášení (deska 19): e-mail → kód z e-mailu → (víc firem) výběr firmy.
 * Heslo zůstává jako záloha pod „Přihlásit heslem“.
 */
export default function Login() {
  const { login, loadMe } = useAuth();
  const nav = useNavigate();
  // Po přihlášení zpět, odkud člověk přišel (třeba odkaz na úkol z e-mailu).
  const odkud = useLocation().state?.from;
  const cil = odkud ? `${odkud.pathname}${odkud.search || ""}` : "/";
  const [krok, setKrok] = useState("email");       // email | kod | firma
  const [heslem, setHeslem] = useState(false);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [vyber, setVyber] = useState(null);         // {vyber_token, clenstvi}
  const [error, setError] = useState("");
  const [kodChyba, setKodChyba] = useState(0);
  const [loading, setLoading] = useState(false);

  const dovnitr = async () => { await loadMe(); nav(cil); };
  const chyba = (err) => setError(apiError(err.response?.data?.detail) || err.message);

  const odeslat = async (e) => {
    e.preventDefault();
    setError(""); setLoading(true);
    try {
      if (heslem) { await login(email, password); nav(cil); return; }
      await api.post("/api/auth/kod", { email });
      setKrok("kod");
    } catch (err) { chyba(err); } finally { setLoading(false); }
  };

  const overit = async (kod) => {
    setError(""); setLoading(true);
    try {
      const { data } = await api.post("/api/auth/kod/overit", { email, kod });
      if (data.vyber) { setVyber(data); setKrok("firma"); } else await dovnitr();
    } catch (err) { chyba(err); setKodChyba((x) => x + 1); } finally { setLoading(false); }
  };

  const vybrat = async (c) => {
    setError(""); setLoading(c.membership_id);
    try {
      await api.post("/api/auth/kod/vybrat", { vyber_token: vyber.vyber_token, membership_id: c.membership_id });
      await dovnitr();
    } catch (err) { chyba(err); setLoading(false); }
  };

  const chybaBox = error && (
    <div className="t-meta text-danger bg-[#fdecea] rounded-input px-3 py-2" data-testid="login-error">{error}</div>
  );

  if (krok === "kod") {
    return (
      <AuthShell title="Zadej kód z e-mailu"
        subtitle={<>Poslali jsme ho na <b className="text-ink font-700">{email}</b>. Platí 5 minut.</>}>
        <div className="space-y-5" data-testid="login-kod-krok">
          <button type="button" onClick={() => { setKrok("email"); setError(""); }} aria-label="Zpět"
            className="tap -mt-3 h-10 w-10 rounded-full bg-white shadow-[inset_0_0_0_1px_#e6e5e1] inline-flex items-center justify-center text-ink">
            <ChevronLeft size={18} strokeWidth={2.2} />
          </button>
          <KodPole key={kodChyba} onHotovo={overit} disabled={!!loading} chyba={kodChyba > 0 && !!error} testid="login-kod" />
          {loading && <div className="flex items-center gap-2 t-meta text-muted"><Spinner /> Ověřuji…</div>}
          {chybaBox}
          <PoslatZnovu onPoslat={async () => { setError(""); try { await api.post("/api/auth/kod", { email }); } catch (err) { chyba(err); } }} />
        </div>
      </AuthShell>
    );
  }

  if (krok === "firma") {
    return (
      <AuthShell title="Vyber firmu" subtitle="Tvůj e-mail je ve víc firmách. Mezi nimi pak přepneš v menu.">
        <div className="space-y-2" data-testid="login-firmy">
          {vyber.clenstvi.map((c, i) => (
            <button key={c.membership_id} type="button" onClick={() => vybrat(c)} disabled={!!loading}
              data-testid={`login-firma-${c.membership_id}`}
              className="w-full min-h-[68px] rounded-[16px] bg-white shadow-[0_1px_2px_rgba(20,22,26,.06),inset_0_0_0_1px_#eceae5] hover:bg-inputbg flex items-center gap-3 px-3.5 py-3 text-left">
              <ZnakFirmy nazev={c.company_name} logo={c.logo_url} />
              <span className="flex-1 min-w-0">
                <span className="block t-telo md:text-[15px] font-700 text-ink truncate">{c.company_name}</span>
                <span className="block t-meta text-muted">
                  {(c.roles || []).map((r) => ROLE_LABEL[r] || r).join(", ")}{i === 0 && c.last_login_at ? " · naposledy" : ""}
                </span>
              </span>
              {loading === c.membership_id ? <Spinner /> : <ChevronRight size={18} strokeWidth={2.2} className="text-muted shrink-0" />}
            </button>
          ))}
          {chybaBox}
        </div>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      title="Přihlášení"
      subtitle={heslem ? "Přihlas se e-mailem a heslem." : "Pošleme ti kód na e-mail — bez hesla."}
      footer={<>Nemáš účet?{" "}<Link to="/register" className="text-accent font-600 hover:text-accent-hover">Založ firmu</Link></>}
    >
      <form onSubmit={odeslat} className="space-y-4" data-testid="login-form">
        <Field label="E-mail">
          <input type="email" className="field" value={email} onChange={(e) => setEmail(e.target.value)} autoComplete="email"
            placeholder="jmeno@firma.cz" data-testid="login-email" required autoFocus />
        </Field>
        {heslem && (
          <Field label="Heslo">
            <input type="password" className="field" value={password} onChange={(e) => setPassword(e.target.value)}
              autoComplete="current-password" placeholder="••••••••••" data-testid="login-password" required />
          </Field>
        )}
        {chybaBox}
        <Button className="w-full justify-center tap" disabled={loading} data-testid="login-submit">
          {loading ? <Spinner /> : heslem ? "Přihlásit se" : "Poslat kód"}
        </Button>
        <div className="flex items-center justify-between -mt-1">
          <button type="button" className="tap md:min-h-0 t-meta font-700 text-sec hover:text-ink" data-testid="login-prepnout-heslo"
            onClick={() => { setHeslem((x) => !x); setError(""); }}>
            {heslem ? "Raději kódem z e-mailu" : "Přihlásit heslem"}
          </button>
          {heslem && <Link to="/forgot-password" className="t-meta text-muted hover:text-sec">Zapomenuté heslo?</Link>}
        </div>
      </form>

      <div className="mt-6 rounded-input bg-inputbg border border-line-strong p-3 text-[11.5px] text-muted-2 leading-relaxed">
        <span className="label-up text-muted block mb-1">Demo přístup</span>
        martin@klimatop.cz · jana@klimatop.cz · petr@klimatop.cz — heslo{" "}
        <span className="amount text-sec">demo123</span>
      </div>
    </AuthShell>
  );
}
